import { useCallback, useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { Control, useForm, useWatch } from 'react-hook-form';

import { AdvancedSearchFormProps, useAdvancedSearch } from '@/components/dialogs/advancedSearch/useAdvancedSearch';
import { SearchParams } from '@/components/search/types';
import { CollapsibleContent } from '@/components/UI/Collapsible/Collapsible';
import MwdDialog from '@/components/UI/Dialog/Dialog';
import { FormErrorNotification } from '@/components/UI/Fields/FormErrorNotification/FormErrorNotification';
import { CarouselButtonField } from '@/components/UI/Fields/ReactFormFields/CarouselButtonField';
import { CheckboxField } from '@/components/UI/Fields/ReactFormFields/CheckboxField';
import { CreatableSelectField } from '@/components/UI/Fields/ReactFormFields/CreatableSelectField';
import { InputField } from '@/components/UI/Fields/ReactFormFields/CreateFieldComponent';
import DienstregelpuntSelectField from '@/components/UI/Fields/ReactFormFields/DienstregelpuntSelectField';
import { ExtendedSearchField } from '@/components/UI/Fields/ReactFormFields/ExtendedSearchField';
import { MaterieelTypenSearchField } from '@/components/UI/Fields/ReactFormFields/MaterieelTypenSearchField';
import { RadioField } from '@/components/UI/Fields/ReactFormFields/RadioField';
import { SelectField } from '@/components/UI/Fields/ReactFormFields/SelectField';
import { TimeInputField } from '@/components/UI/Fields/ReactFormFields/TimeInputField';
import { Label } from '@/components/UI/Label/Label';

import styles from '@/components/UI/Dialog/Dialog.module.css';
import fieldsStyles from '@/components/UI/Fields/Fields.module.css';

export type AdvancedSearchDialogProps = {
  showDialog: boolean;
  handleOpenChange: (value: boolean) => void;
  handleAdvancedSearchData?: (data, voegWerklijnenToe: boolean) => void;
  handleAdvancedSearchParams?: (params: SearchParams) => void;
  clearAdvancedSearchData?: boolean;
  title: string;
};

const AdvancedSearchDialog = ({
  showDialog,
  handleOpenChange,
  handleAdvancedSearchData,
  handleAdvancedSearchParams,
  clearAdvancedSearchData,
  title,
}: AdvancedSearchDialogProps) => {
  const { handleSubmit, setValue, reset, control } = useForm<AdvancedSearchFormProps>({
    mode: 'onSubmit',
    defaultValues: {
      toegewezenWerklijnen: true,
    },
  });
  const existingDienstregelpunten = sessionStorage.getItem('advancedSearchDrglpunten');

  const submitTypeRef = useRef('submit');
  const [showPeriodSelection, setShowPeriodSelection] = useState(true);
  const [showVerkeersDatum, setShowVerkeersDatum] = useState(false);
  const [showLocation, setShowLocation] = useState(false);
  const [showClaims, setShowClaims] = useState(true);
  const [showBijzonderheden, setShowBijzonderheden] = useState(true);
  const [showMaterieelTypen, setShowMaterieelTypen] = useState(true);
  const [disableBijzonderheden, setDisableBijzonderheden] = useState(false);
  const [__disableDefecten, setDisableDefecten] = useState(true);
  const handleSubmitType = (type) => {
    submitTypeRef.current = type;
  };

  const extendedSearch = useWatch({ control, name: 'extendedSearch' });
  const locationWatch = useWatch({ control, name: 'dienstregelpunten' });
  const claimWatch = useWatch({ control, name: 'claims' });
  const toegewezenWerklijnenWatch = useWatch({ control, name: 'toegewezenWerklijnen' });
  const bijzonderhedenWatch = useWatch({ control, name: 'bijzonderheden' });

  const resetDefectenCheckboxes = useCallback(() => {
    setValue('defectNietInzetbaar', false);
    setValue('defectBeperktInzetbaar', false);
    setValue('defectInzetbaar', false);
  }, [setValue]);

  const {
    onSubmit,
    submitDisabled,
    setSubmitDisabled,
    handleResetFields,
    dateVariationsForVerkeersdatum,
    dateVariationsWithoutRestOfTheDay,
    dateVariationsWithManualOption,
    createOption,
    resetFields,
    AlleDrglptnamen,
    handleResetExtendedSearch,
    handlePeriodeChange,
    incorrectDate,
    noSearchValuesValidation,
    filteredClaimSoortOptions,
    handleMaterieelTypenChange,
    selectedMaterieelTypen,
    setHandmatig,
    handmatig,
  } = useAdvancedSearch(
    setValue,
    handleAdvancedSearchParams,
    clearAdvancedSearchData,
    submitTypeRef,
    handleOpenChange,
    handleAdvancedSearchData,
  );

  useEffect(() => {
    if (showDialog) {
      reset({
        toegewezenWerklijnen: true,
        materieelTypen: selectedMaterieelTypen,
      });
      setShowVerkeersDatum(false);
      setShowPeriodSelection(true);
      setDisableDefecten(true);
    } else {
      setHandmatig(false);
    }
  }, [showDialog]);

  useEffect(() => {
    if (toegewezenWerklijnenWatch || toegewezenWerklijnenWatch === undefined) {
      setDisableBijzonderheden(false);
      setDisableDefecten(false);
    } else {
      setDisableBijzonderheden(true);
      setDisableDefecten(true);
      resetDefectenCheckboxes();
    }
  }, [resetDefectenCheckboxes, toegewezenWerklijnenWatch]);

  useEffect(() => {
    if (
      (bijzonderhedenWatch === 'defect' && toegewezenWerklijnenWatch) ||
      (bijzonderhedenWatch === 'defect' && toegewezenWerklijnenWatch === undefined)
    ) {
      setDisableDefecten(false);
    } else {
      setDisableDefecten(true);
      resetDefectenCheckboxes();
    }
  }, [bijzonderhedenWatch, resetDefectenCheckboxes, toegewezenWerklijnenWatch]);

  useEffect(() => {
    const searchTypeStateMap = {
      trainAndRollingStock: {
        period: false,
        verkeersDatum: true,
        location: false,
        claims: false,
        materieelType: false,
        bijzonderheden: false,
      },
      train: {
        period: false,
        verkeersDatum: true,
        location: false,
        claims: false,
        materieelType: false,
        bijzonderheden: false,
      },
      omloop: {
        period: false,
        verkeersDatum: true,
        location: false,
        claims: false,
        materieelType: false,
        bijzonderheden: false,
      },
      rollingStock: {
        period: false,
        verkeersDatum: false,
        location: false,
        claims: false,
        materieelType: false,
        bijzonderheden: false,
      },
    };

    const defaultState = {
      period: true,
      verkeersDatum: false,
      location: true,
      claims: true,
      materieelType: true,
      bijzonderheden: true,
    };

    const state = extendedSearch
      ? searchTypeStateMap[(extendedSearch as SearchParams).searchType] || defaultState
      : defaultState;

    setShowPeriodSelection(state.period);
    setShowVerkeersDatum(state.verkeersDatum);
    setShowLocation(state.location);
    setShowClaims(state.claims);
    setShowMaterieelTypen(state.materieelType);
    setShowBijzonderheden(state.bijzonderheden);

    if (!extendedSearch) {
      setValue('verkeersDatum', undefined);
    }
  }, [extendedSearch, setValue, locationWatch, claimWatch]);

  return (
    <MwdDialog
      testId='advancedSearchDialog'
      className={styles.advancedSearchDialogStyle}
      hasError={submitDisabled}
      resetErrorState={() => setSubmitDisabled(false)}
      title={title}
      scrollEnabled
      headerSeparator={true}
      hasCloseInHeader={true}
      showDialog={showDialog}
      handleOpenChange={handleOpenChange}
    >
      <form onFocus={() => handleResetFields(false)} onSubmit={handleSubmit(onSubmit)}>
        <div style={{ marginBottom: '16px' }}>
          <ExtendedSearchField
            label='Zoek op trein, eenheid of omloop'
            hideSearchIcon
            handleResetFieldWithinAdvancedSearch={handleResetExtendedSearch}
            clearSearchField={resetFields}
            isWithinAdvancedSearch
            control={control}
            name='extendedSearch'
          />
        </div>
        <CollapsibleContent open={showLocation} onOpenChange={setShowLocation}>
          <div
            style={{
              fontWeight: 500,
              marginTop: '32px',
              marginBottom: '8px',
              color: 'var(--tractie-color-content-emphasis-default)',
            }}
          >
            Of zoek op eigenschappen
          </div>
          <div style={{ marginBottom: '8px' }}>
            <DienstregelpuntSelectField
              control={control}
              name='dienstregelpunten'
              defaultValue={
                existingDienstregelpunten ? JSON.parse(existingDienstregelpunten).map((item) => createOption(item)) : []
              }
              alleDrglptnamen={AlleDrglptnamen ?? []}
            />
          </div>
        </CollapsibleContent>
        <CollapsibleContent open={showMaterieelTypen} onOpenChange={setShowMaterieelTypen}>
          <MaterieelTypenSearchField
            name='materieelTypen'
            marginBottom={2}
            control={control as Control<any>}
            defaultValue={selectedMaterieelTypen.map((item) => createOption(item))}
            enableSelectOneForMultiple={false}
            onChangeCallback={handleMaterieelTypenChange}
          />
          <MaterieelTypenSearchField
            label='Gepland materieeltype'
            name='geplandMaterieelTypen'
            testId='geplandMaterieelTypen'
            marginBottom={2}
            control={control as Control<any>}
            enableSelectOneForMultiple={false}
          />
        </CollapsibleContent>
        <CollapsibleContent open={showClaims} onOpenChange={setShowClaims}>
          <div style={{ marginBottom: '8px' }}>
            <CreatableSelectField
              label='Claimsoorten'
              control={control}
              testId='claims'
              createLabel='Claimsoorten:'
              isMulti={true}
              isClearable
              isSearchable
              openMenuOnClick={true}
              name='claims'
              matchFromStart={true}
              options={filteredClaimSoortOptions || []}
              placeholder='Voeg claim(s) toe'
            />
          </div>
        </CollapsibleContent>
        <CollapsibleContent open={showBijzonderheden} onOpenChange={setShowBijzonderheden}>
          <div style={{ marginBottom: '0px' }}>
            <Label>Bijzonderheden</Label>
            <CheckboxField control={control} name='mancoWerklijnen' label='Manco werklijnen' />
            <CheckboxField control={control} name='toegewezenWerklijnen' label='Toegewezen werklijnen' />
            {toegewezenWerklijnenWatch && (
              <div style={{ marginTop: '4px', marginLeft: '20px' }}>
                <RadioField
                  name='bijzonderheden'
                  radioBar={false}
                  disabled={disableBijzonderheden}
                  control={control}
                  defaultValue='alles'
                  options={[
                    { label: 'Alles', value: 'alles' },
                    {
                      label: (
                        <div style={{ display: 'flex', alignItems: 'baseline', flexDirection: 'column', gap: '4px' }}>
                          <div>Defect</div>
                          {bijzonderhedenWatch === 'defect' && (
                            <div>
                              <CheckboxField control={control} name='defectNietInzetbaar' label='Niet inzetbaar' />
                              <CheckboxField
                                control={control}
                                name='defectBeperktInzetbaar'
                                label='Beperkt inzetbaar'
                              />
                              <CheckboxField control={control} name='defectInzetbaar' label='Inzetbaar' />
                            </div>
                          )}
                        </div>
                      ),
                      value: 'defect',
                    },
                    { label: 'Verkeerd materieel', value: 'verkeerdMaterieel' },
                    { label: 'Versterkt materieel', value: 'versterktMaterieel' },
                    {
                      label: (
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          Inregeling vanaf
                          <InputField
                            className={`${fieldsStyles.inputFieldStyle} `}
                            disabled={disableBijzonderheden}
                            defaultValue={format(new Date(), 'yyyy-MM-dd')}
                            narrowVariant
                            displayBlock={false}
                            showOptional={false}
                            {...{ control, name: 'inregelingVanafDatum', type: 'date' }}
                          />
                        </div>
                      ),
                      value: 'inregelingVanaf',
                    },
                    {
                      label: (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {' '}
                          Overstandsduur vanaf{' '}
                          <TimeInputField
                            dataTestId='overstandsduur'
                            disabled={disableBijzonderheden}
                            control={control}
                            defaultValue='02:00'
                            name='overstandsduurTijd'
                          />
                        </div>
                      ),
                      value: 'overstandsduur',
                    },
                  ]}
                />
              </div>
            )}
          </div>
        </CollapsibleContent>
        <CollapsibleContent open={showVerkeersDatum} onOpenChange={setShowVerkeersDatum}>
          <div style={{ marginBottom: '24px' }}>
            <CarouselButtonField
              autoFocus
              control={control}
              name='verkeersDatum'
              label='Verkeersdatum'
              items={dateVariationsForVerkeersdatum}
            />
          </div>
        </CollapsibleContent>
        <CollapsibleContent open={showPeriodSelection} onOpenChange={setShowPeriodSelection}>
          <div style={{ marginBottom: '0' }}>
            <CarouselButtonField
              data-dtname='geavanceerd-zoeken-periode'
              control={control}
              name='periode'
              defaultValue={handmatig ? 'manual' : undefined}
              onChangeCallback={(value) => handlePeriodeChange(value)}
              label='Periode'
              items={dateVariationsWithManualOption}
            />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <SelectField
              shortInputField
              testId='datumVanDag'
              minWidth='120px'
              control={control}
              onChangeCallback={() => setHandmatig(true)}
              defaultValue={dateVariationsWithoutRestOfTheDay[0].value}
              name='datumVanDag'
              label='Van dag'
              options={dateVariationsWithoutRestOfTheDay}
            />
            <InputField
              geavanceerdZoekenVariant
              onChangeCallback={() => setHandmatig(true)}
              control={control}
              data-testid='datumVanTijd'
              type='time'
              role='input'
              required
              width='90px'
              defaultValue={format(new Date(), 'HH:mm')}
              name='datumVanTijd'
              label='Van tijd'
            />
            <div
              style={{
                display: 'flex',
                alignSelf: 'center',
                marginTop: '20px',
                color: 'var(--tractie-color-content-default)',
              }}
            >
              {' '}
              -{' '}
            </div>
            <SelectField
              shortInputField
              minWidth='120px'
              testId='datumTotEnMetDag'
              control={control}
              onChangeCallback={() => setHandmatig(true)}
              name='datumTotEnMetDag'
              label='Tot dag'
              defaultValue={dateVariationsWithoutRestOfTheDay[1].value}
              options={dateVariationsWithoutRestOfTheDay}
            />
            <InputField
              geavanceerdZoekenVariant
              onChangeCallback={() => setHandmatig(true)}
              control={control}
              defaultValue='02:00'
              data-testid='datumTotEnMetTijd'
              type='time'
              role='input'
              required
              width='90px'
              name='datumTotEnMetTijd'
              label='Tot tijd'
            />
          </div>
        </CollapsibleContent>
        <CollapsibleContent open={submitDisabled} onOpenChange={setSubmitDisabled}>
          {incorrectDate && submitDisabled ? (
            <FormErrorNotification errorMessage='De eindtijd moet na de starttijd liggen' />
          ) : noSearchValuesValidation && submitDisabled ? (
            <FormErrorNotification errorMessage='Vul minimaal één zoekveld in' />
          ) : null}
        </CollapsibleContent>
        <MwdDialog.Footer
          submitCaption='Toevoegen'
          secondarySubmitCaption='Vervangen'
          cancelCallback={() => handleResetFields(true)}
          cancelCaption='Verwijder criteria'
          justifyContentStyle='space-between'
          handleSubmitType={(type) => handleSubmitType(type)}
        />
      </form>
    </MwdDialog>
  );
};

export default AdvancedSearchDialog;
